import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { PackageService } from '@lessor/core/services/package/package.service';
import { AthletePackageCouponResponse, AthletePackageResponse } from '@libs/entity-lib';
import { parseBrDateTime } from '@libs/util-ui';
import { addIcons } from 'ionicons';
import {
  checkmarkCircle,
  chevronDown,
  chevronUp,
  closeCircleOutline,
  closeOutline,
  ticketOutline,
} from 'ionicons/icons';

import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';

/** Cupom já enriquecido com o pacote de origem, que é o que a reserva precisa saber. */
export interface SelectedPackageCoupon {
  couponId: number;
  packageName: string;
  rentPackageId: number;
  validityDate?: string | null;
}

/**
 * Escolha do cupom a usar numa reserva feita pelo painel.
 *
 * Existe porque a lista inline no modal de reserva não escala: cliente com 16
 * pacotes virava um paredão de linhas truncadas dentro do formulário. Aqui a
 * navegação é em dois níveis — pacote, depois cupom.
 *
 * Props são propriedades simples (não `input()` de signal): o `AngularDelegate` do
 * Ionic só usa `setInput` com `useSetInputAPI` ligado, e neste app não está — ele
 * faz `Object.assign`, que substituiria o InputSignal pelo valor cru.
 */
@Component({
  selector: 'lessor-athlete-packages-modal',
  templateUrl: './athlete-packages-modal.component.html',
  styleUrls: ['./athlete-packages-modal.component.scss'],
  imports: [
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonSpinner,
    IonTitle,
    IonToolbar,
  ],
  providers: [PackageService],
})
export class AthletePackagesModalComponent implements OnInit {
  private readonly modalCtrl = inject(ModalController);
  private readonly packageService = inject(PackageService);

  athleteName = 'cliente';
  /** Pacotes já carregados pelo modal de reserva — evita refazer a mesma chamada. */
  packages: AthletePackageResponse[] = [];
  selectedCouponId: number | null = null;

  readonly packageList = signal<AthletePackageResponse[]>([]);
  readonly openedRentPackageId = signal<number | null>(null);
  readonly coupons = signal<AthletePackageCouponResponse[]>([]);
  readonly loadingCoupons = signal(false);
  readonly couponsError = signal<string | null>(null);

  /** Só cupom livre pode ser gasto; usado/cancelado aparece desabilitado com o motivo. */
  readonly availableCoupons = computed(() =>
    this.coupons().filter((coupon) => coupon.available && !coupon.deleted && !coupon.used),
  );

  readonly unavailableCoupons = computed(() =>
    this.coupons().filter((coupon) => !coupon.available || coupon.deleted || coupon.used),
  );

  constructor() {
    // Mesma convencao do event-creator: os icones deste modal sao registrados
    // aqui, nao dependem do fallback de assets.
    addIcons({
      checkmarkCircle,
      chevronDown,
      chevronUp,
      closeCircleOutline,
      closeOutline,
      ticketOutline,
    });
  }

  ngOnInit() {
    this.packageList.set(this.packages ?? []);

    // Um pacote só: abrir direto economiza um toque sem esconder nada.
    const only = this.packageList().length === 1 ? this.packageList()[0] : null;
    if (only?.rentPackageId) this.openPackage(only);
  }

  openPackage(pack: AthletePackageResponse) {
    if (!pack.rentPackageId) return;

    if (this.openedRentPackageId() === pack.rentPackageId) {
      this.openedRentPackageId.set(null);
      this.coupons.set([]);
      return;
    }

    this.openedRentPackageId.set(pack.rentPackageId);
    this.coupons.set([]);
    this.couponsError.set(null);
    this.loadingCoupons.set(true);

    this.packageService.fetchCouponsByRentPackage(pack.rentPackageId).subscribe({
      next: (coupons) => {
        this.coupons.set(coupons ?? []);
        this.loadingCoupons.set(false);
      },
      error: () => {
        this.couponsError.set('Não foi possível carregar os cupons deste pacote.');
        this.loadingCoupons.set(false);
      },
    });
  }

  chooseCoupon(coupon: AthletePackageCouponResponse) {
    const pack = this.packageList().find(
      (item) => item.rentPackageId === this.openedRentPackageId(),
    );
    if (!pack) return;

    const selected: SelectedPackageCoupon = {
      couponId: coupon.couponId,
      packageName: pack.packageName,
      rentPackageId: pack.rentPackageId,
      validityDate: coupon.validityDate,
    };
    this.modalCtrl.dismiss(selected, 'selected');
  }

  packageSummary(pack: AthletePackageResponse): string {
    const available = pack.couponsAvailable ?? 0;
    const total = pack.totalCoupons ?? 0;
    return `${available} de ${total} disponíveis`;
  }

  /** Datas do pacote chegam `dd/MM/yyyy HH:mm` — `new Date()` não lê esse formato. */
  validityLabel(value?: string | null): string | null {
    const date = parseBrDateTime(value);
    if (!date) return null;
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
  }

  couponStatusLabel(coupon: AthletePackageCouponResponse): string {
    if (coupon.deleted) return 'cancelado';
    if (coupon.used) {
      const used = this.validityLabel(coupon.usageDate);
      return used ? `usado em ${used}` : 'usado';
    }
    return 'disponível';
  }

  isSelected(coupon: AthletePackageCouponResponse): boolean {
    return this.selectedCouponId === coupon.couponId;
  }

  clearSelection() {
    this.modalCtrl.dismiss(null, 'cleared');
  }

  close() {
    this.modalCtrl.dismiss(null, 'cancel');
  }
}
